import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { HuntGroupsReportParameters } from '@ndt/report-output/hunt-groups/hunt-groups';
import { BooleanReportParameter } from '@ndt/report-output/shared/models/boolean-report-parameter';
import { ReportCallDispositions } from '@ndt/report-output/shared/models/call-disposition-report-parameter';
import {
   ExtensionReportSortBy,
   ExtensionSortByReportParameter,
} from '@ndt/report-output/shared/models/extension-sort-by-report-parameter';
import { defaultAllMultiEntitySelectParameter } from '@ndt/report-output/shared/models/multi-entity-selection-report-parameter';
import { TimeZoneReportParameter } from '@ndt/report-output/shared/models/time-zone-report-parameters';
import { createDateRangeReportParameter } from '@ndt/reports/shared/models/date-range-report-parameter-value';
import { defaultTimeZoneParameter } from '@ndt/reports/shared/models/time-zone-parameter-value';
import {
   convertFromFormValueList,
   ParameterFormValueUnion,
} from '@ndt/reports/shared/report-parameter-list/models/report-parameter-form-value';
import {
   CallDispositionsToIncludeReportParameter,
   convertToDateRangeReportParameter,
   convertToForm,
   EntitiesToIncludeParameter,
   ReportParametersUnion,
} from '@ndt/reports/shared/report-parameter-list/models/report-parameter-union.model';
import { ReportParameterConfig } from '@ndt/reports/shared/report-parameter-list/report-parameter.config';
import { ReportParameters } from 'generated/graphql-types';
import { LcsDateTimeAdapter } from 'src/app/common-components/date-time-picker/configuration/date-time-adapter';
import { throwIfNullOrUndefined } from 'src/app/common-components/utils/type-safety';
import {
   ReportParameterFormService,
   ReportParameterFormServiceBase,
} from '../../shared/report-parameter-list/services/report-parameter-form.service';

export class HuntGroupsParametersFormService
   extends ReportParameterFormServiceBase
   implements ReportParameterFormService<HuntGroupsReportParameters>
{
   config: ReportParameterConfig[];

   form: UntypedFormGroup;

   constructor(fb: UntypedFormBuilder, private dateAdapter: LcsDateTimeAdapter<Date>) {
      super(fb);
      const initialValue: ReportParametersUnion[] = [
         {
            accountId: 0,
            all: true,
            names: [],
            parameterType: ReportParameters.ExtensionsToInclude,
            sortOrder: 1,
            values: [],
         },
         {
            parameterType: ReportParameters.DateRange,
            ...createDateRangeReportParameter(),
            sortOrder: 3,
         },
         {
            parameterType: ReportParameters.ExtensionSortBy,
            sortOrder: 4,
            value: ExtensionReportSortBy.Number,
         },
         {
            parameterType: ReportParameters.SummaryOnly,
            sortOrder: 5,
            value: false,
         },
         {
            parameterType: ReportParameters.TimeZone,
            sortOrder: 6,
            value: '',
         },
      ];

      this.config = initialValue.map((ap) => {
         return this.getReportParameterConfig(ap.parameterType, false, true);
      });

      this.form = convertToForm(initialValue, fb);
   }

   patchSavedValues(values: ReportParametersUnion[], showAskToggle: boolean, showAskParameters: boolean): void {
      throw new Error('Method not implemented.');
   }

   resetConfig(): void {
      throw new Error('Method not implemented.');
   }

   // getLayoutConfigurations(values: ReportParametersUnion[]): {
   //    name: string | null;
   //    color: string | null;
   //    refreshTimeSeconds: number | null;
   // } {
   //    throw new Error('Method not implemented.');
   // }

   setValue(values: ParameterFormValueUnion[]): void {
      this.form.setValue(values, { emitEvent: false });
   }

   getValue(includeAskParameters?: boolean | undefined): HuntGroupsReportParameters | null {
      const formValue = this.form.getRawValue();
      const values: ParameterFormValueUnion[] = formValue.values;
      const convertedValues = convertFromFormValueList(values);
      return this.convertValue(convertedValues);
   }

   convertValue(values: ReportParametersUnion[]): HuntGroupsReportParameters | null {
      const extensions = values.find((v) => {
         return v.parameterType === ReportParameters.ExtensionsToInclude;
      }) as EntitiesToIncludeParameter | undefined;

      const extensionSortBy = values.find((v) => {
         return v.parameterType === ReportParameters.ExtensionSortBy;
      }) as ExtensionSortByReportParameter | undefined;

      const callDispositionsToInclude = values.find((v) => {
         return v.parameterType === ReportParameters.CallDispositionsToInclude;
      }) as CallDispositionsToIncludeReportParameter | undefined;

      const summaryOnly = values.find((v) => {
         return v.parameterType === ReportParameters.SummaryOnly;
      }) as BooleanReportParameter | undefined;

      const timeZone = values.find((v) => {
         return v.parameterType === ReportParameters.TimeZone;
      }) as TimeZoneReportParameter | undefined;

      const output: HuntGroupsReportParameters = {
         callDispositionsToInclude: callDispositionsToInclude
            ? {
                 value: callDispositionsToInclude.value,
              }
            : {
                 value: ReportCallDispositions.Both,
              },
         dateRange: throwIfNullOrUndefined(convertToDateRangeReportParameter(values, this.dateAdapter)),
         extensions: extensions
            ? {
                 accountId: extensions.accountId,
                 all: !!extensions?.all,
                 values: extensions.values || [],
              }
            : defaultAllMultiEntitySelectParameter,
         extensionSortBy: extensionSortBy
            ? {
                 value: extensionSortBy.value,
              }
            : {
                 value: ExtensionReportSortBy.Number,
              },
         summaryOnly: summaryOnly
            ? {
                 value: summaryOnly.value,
              }
            : {
                 value: false,
              },
         timeZone: timeZone
            ? {
                 value: timeZone.value,
              }
            : defaultTimeZoneParameter,
      };

      return output;
   }

   isListComplete(values: ReportParametersUnion[], includeAskParameters = false): boolean {
      const validParameters = !includeAskParameters
         ? values.filter((v) => {
              return !false;
           })
         : values;

      const dateRange = validParameters.find((v) => {
         return v.parameterType === ReportParameters.DateRange;
      });

      return !!dateRange;
   }
}
